import { LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-blog-post-hero.inner.pcss?inline';
import { SlotControllerLegacy } from '../../controllers/slot-controller-legacy';
import '../se-background/se-background';
import '../se-heading/se-heading';
import '../se-link/se-link';
import '../outline-icon/outline-icon';
import { classMap } from 'lit/directives/class-map.js';
import { whenParsed } from '../../utilities/when-parsed';

/**
 * The SeBlogPostHero component
 * @element se-blog-post-hero
 */
@customElement('se-blog-post-hero')
export class SeBlogPostHero extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });
  slots = new SlotControllerLegacy(this, true);

  @property({ type: String })
  title: string;
  @property({ type: String, attribute: 'image-caption' })
  imageCaption: string;
  @property({ type: String })
  series: string;
  @property({ type: String })
  social: string;

  @state() hasHeroEyebrowLink: boolean;

  async firstUpdated() {
    // This component reads its own light-DOM children. In the critical
    // bundle it upgrades mid-parse, before those children exist, so wait
    // for them. See utilities/when-parsed.ts (SE-1004).
    await whenParsed();

    this.hasHeroEyebrowLink = this.slots.test('hero-eyebrow-link');
  }

  heroImage(): TemplateResult {
    if (this.querySelector('[slot="hero-image"]')) {
      return html`<div class="hero-image">
        <slot name="hero-image"></slot> ${this.imageCaption
          ? html`<div class="image-caption">
            ${`${this.imageCaption}`}
          </div></div>`
          : ''}
      </div>`;
    } else {
      return html``;
    }
  }

  heroSocial(): TemplateResult {
    if (this.querySelector('[slot="hero-social"]')) {
      return html`<div class="social">
        <slot name="hero-social"></slot>
      </div>`;
    } else {
      return html``;
    }
  }

  heroButtons(): TemplateResult {
    if (this.querySelector('[slot="hero-buttons"]')) {
      return html`<div class="buttons">
        <slot name="hero-buttons"></slot>
      </div>`;
    } else {
      return html``;
    }
  }

  render(): TemplateResult {
    const imageSlot = this.querySelector('[slot="hero-image"]');
    const classes = {
      'se-blog-post-hero': true,
      'se-blog-post-hero--with-image': !!imageSlot,
      'se-blog-post-hero--no-image': !imageSlot,
    };
    return html`<div class="${classMap(classes)}">
      <div class="background-container">
        <se-background
          alignment="left"
          color="primary-2"
          variant="wave"
          curve-width="75"
          class="background"
        >
          <div class="background-dimensions"></div>
        </se-background>
      </div>
      <se-container variant="narrow">
        ${this.heroImage()}
        <div class="content">
          ${this.hasHeroEyebrowLink || this.series
            ? html`<div class="hero-eyebrow">
                ${this.hasHeroEyebrowLink
                  ? html`<slot name="hero-eyebrow-link"></slot>`
                  : nothing}
                ${this.hasHeroEyebrowLink && this.series
                  ? html`<span class="separator">|</span>`
                  : nothing}
                ${this.series
                  ? html`<p class="series">${this.series}</p>`
                  : nothing}
              </div>`
            : nothing}
          <se-heading
            level="h1"
            level-size="h3"
            level-style="normal"
            font="dmsans"
            class="title"
            >${this.title}</se-heading
          >
          <slot name="hero-content"> </slot>
          ${this.heroSocial()}
          ${this.heroButtons()}
          <hr />
        </div>
      </se-container>
    </div>`;
  }
}
